import { useEffect, useState } from 'react';
import { Badge, Button, Card, ErrorState, Input, Switch } from '../design-system/components';
import { api, type ApiError } from '../lib/api';

/** Minute presets offered for the daily buddy-chat budget; `null` = no limit. */
const IM_MINUTE_OPTIONS: Array<number | null> = [15, 30, 45, 60, 90, 120, null];

interface Kid {
  id: string;
  screenName: string;
  birthYear: number | null;
  active: boolean;
  lastLoginAt: string | null;
  /** The kid's PIN, viewable by parents. Null for kids created before PINs
   *  were stored recoverably — reset the PIN once to make it viewable. */
  pin: string | null;
  /** Buddy chat (1:1 DM) gate — off by default. Both kids in a pair need it on. */
  imEnabled: boolean;
  /** Daily buddy-chat budget in minutes; null = unlimited. */
  imDailyMinutes: number | null;
  /** Minutes of buddy chat used so far today (family-local day). */
  imUsedMinutesToday: number;
  /** Allowed-hours window (minutes since midnight); both null = no restriction. */
  imStartMinute: number | null;
  imEndMinute: number | null;
}

/** "13:05" ⇄ minutes-since-midnight, for the <input type="time"> pickers. */
function minutesToClock(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}
function clockToMinutes(clock: string): number {
  const [h, m] = clock.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
const DEFAULT_WINDOW = { imStartMinute: 15 * 60, imEndMinute: 19 * 60 }; // 3pm-7pm
interface FamilyData {
  family: { id: string; name: string; familyCode: string };
  kids: Kid[];
}

export function Family() {
  const [data, setData] = useState<FamilyData | null>(null);
  const [screenName, setScreenName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<ApiError | null>(null);
  const [revealedPin, setRevealedPin] = useState<{ name: string; pin: string } | null>(null);
  const [shownPins, setShownPins] = useState<Set<string>>(new Set());

  function togglePin(kidId: string) {
    setShownPins((prev) => {
      const next = new Set(prev);
      if (next.has(kidId)) next.delete(kidId);
      else next.add(kidId);
      return next;
    });
  }

  async function load() {
    setLoadError(null);
    try {
      setData(await api<FamilyData>('/api/family'));
    } catch (err) {
      setData(null);
      setLoadError(err as ApiError);
    }
  }
  useEffect(() => {
    load();
  }, []);

  async function addKid(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await api<{ kid: Kid; pin: string }>('/api/family/kids', {
        method: 'POST',
        body: JSON.stringify({ screenName }),
      });
      setRevealedPin({ name: res.kid.screenName, pin: res.pin });
      setScreenName('');
      await load();
    } catch (err) {
      const e = err as ApiError;
      setError(e.error === 'screen_name_taken' ? 'That screen name is taken.' : 'Could not add this kid.');
    }
  }

  async function resetPin(kid: Kid) {
    const res = await api<{ pin: string }>(`/api/family/kids/${kid.id}/reset-pin`, { method: 'POST', body: '{}' });
    setRevealedPin({ name: kid.screenName, pin: res.pin });
  }

  async function removeKid(kid: Kid) {
    if (!window.confirm(`Permanently remove ${kid.screenName}? This can't be undone.`)) return;
    await api(`/api/family/kids/${kid.id}`, { method: 'DELETE' });
    await load();
  }

  async function updateIm(
    kid: Kid,
    patch: { imEnabled?: boolean; imDailyMinutes?: number | null; imStartMinute?: number | null; imEndMinute?: number | null },
  ) {
    await api(`/api/family/kids/${kid.id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    await load();
  }

  if (loadError) {
    const message =
      loadError.error === 'forbidden'
        ? "This page is for parents managing a family. As an admin, you don't have a family here."
        : loadError.error === 'no_family'
          ? "You don't currently belong to a family. Ask another grown-up in your household for an invite code, or contact support."
          : "Couldn't load your family.";
    return <ErrorState message={message} onRetry={loadError.error === 'forbidden' || loadError.error === 'no_family' ? undefined : load} />;
  }
  if (!data) return <p className="kz-muted">Loading…</p>;

  return (
    <div className="kz-stack" style={{ gap: 'var(--space-6)' }}>
      <div>
        <span className="kz-eyebrow">My kids</span>
        <h1 style={{ marginTop: 'var(--space-1)' }}>Your family</h1>
      </div>

      {data && (
        <Card style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p className="kz-muted" style={{ font: 'var(--type-small)' }}>Your family login code</p>
            <p className="kz-mono" style={{ fontSize: 'var(--text-xl)', color: 'var(--blue-700)' }}>{data.family.familyCode}</p>
          </div>
          <p className="kz-muted" style={{ maxWidth: 260, font: 'var(--type-small)' }}>
            Kids use this code with their screen name and PIN to sign in.
          </p>
        </Card>
      )}

      {revealedPin && (
        <Card style={{ background: 'var(--sun-100)', borderColor: 'var(--sun-200)' }}>
          <h3>PIN for {revealedPin.name}</h3>
          <p className="kz-mono" style={{ fontSize: 'var(--text-3xl)', letterSpacing: 4, color: 'var(--ink-900)' }}>{revealedPin.pin}</p>
          <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
            Share this with {revealedPin.name} so they can sign in. You can view it again any time below, or reset it.
          </p>
          <Button variant="ghost" size="sm" onClick={() => setRevealedPin(null)}>Got it</Button>
        </Card>
      )}

      <Card>
        <h3 style={{ marginBottom: 'var(--space-3)' }}>Add a kid</h3>
        <form className="kz-row" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }} onSubmit={addKid}>
          <div className="kz-grow">
            <Input label="Screen name" value={screenName} onChange={(e) => setScreenName(e.target.value)} required />
          </div>
          <Button type="submit">Add kid</Button>
        </form>
        {error && <p className="kz-error-text" style={{ marginTop: 'var(--space-2)' }}>{error}</p>}
      </Card>

      <div className="kz-stack">
        {data?.kids.map((kid) => (
          <Card key={kid.id} className="kz-stack" style={{ gap: 'var(--space-3)' }}>
            <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="kz-row">
                <strong>{kid.screenName}</strong>
                {kid.active ? <Badge tone="safe">Active</Badge> : <Badge tone="neutral">Paused</Badge>}
              </div>
              <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
                <Button variant="secondary" size="sm" onClick={() => resetPin(kid)}>Reset PIN</Button>
                <Button variant="ghost" size="sm" style={{ color: 'var(--flag-500)' }} onClick={() => removeKid(kid)}>Remove</Button>
              </div>
            </div>
            <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="kz-muted" style={{ font: 'var(--type-small)' }}>Secret PIN</span>
              {kid.pin ? (
                <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
                  <span className="kz-mono" style={{ fontSize: 'var(--text-lg)', letterSpacing: 3, color: 'var(--ink-900)' }}>
                    {shownPins.has(kid.id) ? kid.pin : '••••••'}
                  </span>
                  <Button variant="ghost" size="sm" onClick={() => togglePin(kid.id)}>
                    {shownPins.has(kid.id) ? 'Hide' : 'Show'}
                  </Button>
                </div>
              ) : (
                <span className="kz-muted" style={{ font: 'var(--type-small)' }}>Reset the PIN to view it</span>
              )}
            </div>
            <div className="kz-stack" style={{ gap: 'var(--space-2)', paddingTop: 'var(--space-2)', borderTop: 'var(--border-width) solid var(--border-subtle)' }}>
              <Switch
                label="Buddy chat"
                checked={kid.imEnabled}
                onChange={(v) => updateIm(kid, { imEnabled: v })}
              />
              <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
                Choose when {kid.screenName} can message a buddy directly, 1:1. Off by default; still fully supervised
                and visible to you when it's on.
              </p>
              {kid.imEnabled && (
                <>
                  <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="kz-muted" style={{ font: 'var(--type-small)' }}>Daily buddy-chat time</span>
                    <select
                      value={kid.imDailyMinutes ?? 'none'}
                      onChange={(e) => updateIm(kid, { imDailyMinutes: e.target.value === 'none' ? null : Number(e.target.value) })}
                      style={{ padding: '8px 12px', borderRadius: 'var(--radius-md)', border: 'var(--border-width) solid var(--border-default)', font: 'var(--type-body)' }}
                    >
                      {IM_MINUTE_OPTIONS.map((m) => (
                        <option key={m ?? 'none'} value={m ?? 'none'}>
                          {m ? `${m} minutes a day` : 'No limit'}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
                    Used today: {kid.imUsedMinutesToday} min{kid.imDailyMinutes ? ` of ${kid.imDailyMinutes}` : ''}
                  </p>
                  <Switch
                    label="Only during certain hours"
                    checked={kid.imStartMinute !== null}
                    onChange={(v) => updateIm(kid, v ? DEFAULT_WINDOW : { imStartMinute: null, imEndMinute: null })}
                  />
                  {kid.imStartMinute !== null && kid.imEndMinute !== null && (
                    <div className="kz-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <span className="kz-muted" style={{ font: 'var(--type-small)' }}>Chat hours</span>
                      <div className="kz-row" style={{ gap: 'var(--space-2)', alignItems: 'center' }}>
                        <input
                          type="time"
                          value={minutesToClock(kid.imStartMinute)}
                          onChange={(e) => updateIm(kid, { imStartMinute: clockToMinutes(e.target.value), imEndMinute: kid.imEndMinute })}
                          style={{ padding: '6px 10px', borderRadius: 'var(--radius-md)', border: 'var(--border-width) solid var(--border-default)', font: 'var(--type-body)' }}
                        />
                        <span className="kz-muted">to</span>
                        <input
                          type="time"
                          value={minutesToClock(kid.imEndMinute)}
                          onChange={(e) => updateIm(kid, { imStartMinute: kid.imStartMinute, imEndMinute: clockToMinutes(e.target.value) })}
                          style={{ padding: '6px 10px', borderRadius: 'var(--radius-md)', border: 'var(--border-width) solid var(--border-default)', font: 'var(--type-body)' }}
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
